import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { X, Trash2, Plus, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHeader } from '@/contexts';
import { useToast } from '@/hooks/use-toast';
import { formatCNPJ } from '@/lib/utils/formatters';
import { cn } from '@/lib/utils';
import { matchesEmpresaSearch, sortContribuintesForDropdown } from '@/lib/utils/contribuintes';
import { useListaContribuintes } from '@/services/Contribuintes/hooks/useListaContribuintes';
import {
  useBuscarTiposAgendamentos,
  useGravarAgendamento,
  useListarParametros,
  type ParametroRelatorioApi,
} from '@/services/Relatorios';

interface ParametroSelecionado {
  id: string;
  parametroOriginal: ParametroRelatorioApi;
  valor: string;
}

interface ModalAgendarProcessamentoProps {
  aberto: boolean;
  onFechar: () => void;
  nomeRelatorio: string;
  relatorioId: number | null;
}

const ANOS = Array.from({ length: 26 }, (_, i) => {
  const ano = new Date().getFullYear() - i;
  return { value: ano.toString(), label: ano.toString() };
});

const MESES = [
  { value: '01', label: 'Janeiro' },
  { value: '02', label: 'Fevereiro' },
  { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Maio' },
  { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
];

export function ModalAgendarProcessamento({
  aberto,
  onFechar,
  nomeRelatorio,
  relatorioId,
}: ModalAgendarProcessamentoProps) {
  const { filtros: filtrosHeader } = useHeader();
  const [parametros, setParametros] = useState<ParametroSelecionado[]>([]);
  const [agendamentoTipoId, setAgendamentoTipoId] = useState<string>('');
  const [empresaInternaId, setEmpresaInternaId] = useState('');
  const [empresaSelectOpen, setEmpresaSelectOpen] = useState(false);
  const [searchEmpresa, setSearchEmpresa] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: parametrosRelatorio, isLoading: isLoadingParametros } = useListarParametros(
    relatorioId,
    {
      enabled: aberto && Boolean(relatorioId),
    }
  );
  const { data: tiposAgendamento, isLoading: isLoadingTipos } = useBuscarTiposAgendamentos({
    enabled: aberto,
  });
  const { data: contribuintes, isLoading: isLoadingContribuintes } = useListaContribuintes(
    undefined,
    { enabled: aberto }
  );
  const gravarAgendamento = useGravarAgendamento();

  const empresaSelecionadaNoHeader = filtrosHeader.empresaId?.trim() || '';
  const empresaEfetiva = empresaSelecionadaNoHeader || empresaInternaId;
  const empresaTravadaPeloHeader = Boolean(empresaSelecionadaNoHeader);

  const contribuintesOrdenados = useMemo(() => {
    return sortContribuintesForDropdown(contribuintes);
  }, [contribuintes]);

  const contribuintesFiltrados = useMemo(() => {
    return contribuintesOrdenados.filter(contribuinte =>
      matchesEmpresaSearch(contribuinte, searchEmpresa)
    );
  }, [contribuintesOrdenados, searchEmpresa]);

  const contribuinteSelecionado = useMemo(() => {
    return contribuintesOrdenados.find(
      contribuinte => contribuinte.CNPJ_Contribuinte === empresaEfetiva
    );
  }, [contribuintesOrdenados, empresaEfetiva]);

  const normalizarNomeParametro = useCallback(
    (nome: string) =>
      nome
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ''),
    []
  );

  const isCaixaEntradaParam = useCallback(
    (parametro: ParametroRelatorioApi) => {
      return normalizarNomeParametro(parametro.nome).includes('caixaentrada');
    },
    [normalizarNomeParametro]
  );

  const obterValorInicialParametro = useCallback(
    (parametro: ParametroRelatorioApi, cnpjAtual: string) => {
      const nomeParametro = parametro.nome.toLowerCase();

      if (normalizarNomeParametro(parametro.nome).includes('caixaentrada') && cnpjAtual) {
        return cnpjAtual;
      }

      if (nomeParametro.includes('ano')) {
        return parametro.valor_padrao && parametro.valor_padrao !== 'null'
          ? parametro.valor_padrao
          : new Date().getFullYear().toString();
      }

      if (nomeParametro.includes('mes')) {
        return parametro.valor_padrao && parametro.valor_padrao !== 'null'
          ? parametro.valor_padrao
          : String(new Date().getMonth() + 1).padStart(2, '0');
      }

      return parametro.valor_padrao && parametro.valor_padrao !== 'null'
        ? parametro.valor_padrao
        : '';
    },
    [normalizarNomeParametro]
  );

  const precisaCaixaEntrada = useMemo(() => {
    return (parametrosRelatorio ?? []).some(isCaixaEntradaParam);
  }, [isCaixaEntradaParam, parametrosRelatorio]);

  useEffect(() => {
    if (!aberto) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [aberto]);

  useEffect(() => {
    if (aberto) {
      setParametros([]);
      setAgendamentoTipoId('');
      setEmpresaInternaId('');
      setEmpresaSelectOpen(false);
      setSearchEmpresa('');
    }
  }, [aberto]);

  useEffect(() => {
    if (!aberto || !parametrosRelatorio) return;

    const parametrosObrigatorios = parametrosRelatorio
      .filter(parametro => parametro.visivel && parametro.obrigatorio)
      .map(parametro => ({
        id: `${Date.now()}-${parametro.id}`,
        parametroOriginal: parametro,
        valor: obterValorInicialParametro(parametro, empresaEfetiva),
      }));

    setParametros(parametrosObrigatorios);
  }, [aberto, empresaEfetiva, obterValorInicialParametro, parametrosRelatorio]);

  useEffect(() => {
    if (!precisaCaixaEntrada) return;

    setParametros(estadoAtual =>
      estadoAtual.map(parametro =>
        isCaixaEntradaParam(parametro.parametroOriginal)
          ? { ...parametro, valor: empresaEfetiva }
          : parametro
      )
    );
  }, [empresaEfetiva, isCaixaEntradaParam, precisaCaixaEntrada]);

  const parametrosDisponiveis = useMemo(() => {
    return (
      parametrosRelatorio?.filter(
        parametro =>
          parametro.visivel &&
          !parametros.some(selecionado => selecionado.parametroOriginal.id === parametro.id)
      ) ?? []
    );
  }, [parametros, parametrosRelatorio]);

  const handleAtualizarValor = (id: string, valor: string) => {
    setParametros(prev => prev.map(p => (p.id === id ? { ...p, valor } : p)));
  };

  const handleLimparParametro = (parametro: ParametroSelecionado) => {
    if (isCaixaEntradaParam(parametro.parametroOriginal) && empresaTravadaPeloHeader) {
      return;
    }

    if (isCaixaEntradaParam(parametro.parametroOriginal)) {
      setEmpresaInternaId('');
      handleAtualizarValor(parametro.id, '');
      return;
    }

    if (parametro.parametroOriginal.obrigatorio) {
      handleAtualizarValor(parametro.id, '');
      return;
    }

    setParametros(prev => prev.filter(p => p.id !== parametro.id));
  };

  const handleAdicionarParametro = () => {
    const proximoParametro = parametrosDisponiveis[0];

    if (!proximoParametro) {
      return;
    }

    setParametros(prev => [
      ...prev,
      {
        id: `${Date.now()}-${proximoParametro.id}`,
        parametroOriginal: proximoParametro,
        valor: obterValorInicialParametro(proximoParametro, empresaEfetiva),
      },
    ]);
  };

  const parametrosSemValor = parametros.filter(parametro => {
    if (isCaixaEntradaParam(parametro.parametroOriginal)) {
      return !empresaEfetiva.trim();
    }

    return !parametro.valor.trim();
  });

  const podeAgendar =
    Boolean(relatorioId) &&
    Boolean(agendamentoTipoId) &&
    parametrosSemValor.length === 0 &&
    (!precisaCaixaEntrada || Boolean(empresaEfetiva.trim())) &&
    !gravarAgendamento.isPending;

  const handleAgendar = () => {
    if (!relatorioId) {
      return;
    }

    if (!agendamentoTipoId) {
      toast({
        variant: 'destructive',
        title: 'Tipo de agendamento obrigatório',
        description: 'Selecione o tipo de agendamento antes de continuar.',
      });
      return;
    }

    if (parametrosSemValor.length > 0) {
      toast({
        variant: 'destructive',
        title: 'Parâmetros pendentes',
        description: 'Preencha todos os parâmetros antes de agendar o processamento.',
      });
      return;
    }

    gravarAgendamento.mutate(
      {
        agendamento_tipo_id: agendamentoTipoId,
        relatorio_id: relatorioId,
        parametros: parametros.map(parametro => ({
          parametro_nome: parametro.parametroOriginal.nome,
          parametro_valor: isCaixaEntradaParam(parametro.parametroOriginal)
            ? empresaEfetiva.replace(/\D/g, '')
            : parametro.valor,
        })),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['relatorios', 'agendamentos'] });
          toast({
            title: 'Agendamento realizado',
            description: 'O processamento do relatório foi agendado com sucesso.',
          });
          onFechar();
        },
        onError: error => {
          toast({
            variant: 'destructive',
            title: 'Erro ao agendar',
            description: error.message || 'Não foi possível agendar o processamento.',
          });
        },
      }
    );
  };

  if (!aberto) return null;

  const content = (
    <>
      <div className="fixed inset-0 z-9999 bg-black/80 backdrop-blur-sm" onClick={onFechar} />
      <div className="fixed inset-0 z-10000 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="w-[95vw] sm:w-[600px] max-h-[90vh] bg-background rounded-lg shadow-2xl overflow-hidden flex flex-col pointer-events-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-lg font-semibold leading-none tracking-tight">
                Agendar Processamento
              </h2>
              <p className="text-sm text-muted-foreground mt-1.5">{nomeRelatorio}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onFechar}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {precisaCaixaEntrada && (
              <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
                <p className="text-sm font-medium text-foreground">
                  {empresaTravadaPeloHeader
                    ? 'Caixa de Entrada definida pelo header global'
                    : 'Caixa de Entrada obrigatória para este agendamento'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {empresaTravadaPeloHeader
                    ? 'A empresa selecionada no topo da Central de Relatórios será usada automaticamente.'
                    : 'Selecione abaixo a empresa para preencher o parâmetro Caixa de Entrada.'}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Parâmetros</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAdicionarParametro}
                  disabled={parametrosDisponiveis.length === 0}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              {isLoadingParametros && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando parâmetros...
                </div>
              )}

              {parametros.map(parametro => (
                <div key={parametro.id} className="flex items-center gap-2">
                  {isCaixaEntradaParam(parametro.parametroOriginal) ? (
                    empresaTravadaPeloHeader ? (
                      <Input
                        className="flex-1"
                        value={
                          contribuinteSelecionado
                            ? `${contribuinteSelecionado.Nome} - ${formatCNPJ(contribuinteSelecionado.CNPJ_Contribuinte)}`
                            : formatCNPJ(empresaEfetiva)
                        }
                        disabled
                      />
                    ) : (
                      <Select
                        open={empresaSelectOpen}
                        onOpenChange={setEmpresaSelectOpen}
                        value={empresaInternaId}
                        onValueChange={setEmpresaInternaId}
                        disabled={isLoadingContribuintes}
                      >
                        <SelectTrigger className="flex-1 bg-background">
                          <SelectValue placeholder="Selecione a empresa" />
                        </SelectTrigger>
                        <SelectContent usePortal={false} className="z-10010">
                          <div className="border-b p-2">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                              <Input
                                value={searchEmpresa}
                                onChange={event => setSearchEmpresa(event.target.value)}
                                placeholder="Buscar por nome ou CNPJ..."
                                className="h-8 pl-9"
                                onClick={event => event.stopPropagation()}
                                onKeyDown={event => event.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-[280px] overflow-auto">
                            {contribuintesFiltrados.length > 0 ? (
                              contribuintesFiltrados.map(contribuinte => {
                                const isSelected =
                                  contribuinte.CNPJ_Contribuinte === empresaInternaId;

                                return (
                                  <SelectItem
                                    key={contribuinte.CNPJ_Contribuinte}
                                    value={contribuinte.CNPJ_Contribuinte}
                                    className={cn(
                                      isSelected ? 'bg-accent data-[state=checked]:bg-accent' : '',
                                      '[&>span:first-child]:hidden hover:bg-accent/50 focus:bg-accent/50 focus:text-foreground'
                                    )}
                                  >
                                    <div className="flex w-full items-center gap-2">
                                      <span
                                        className={cn(
                                          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                                          isSelected
                                            ? 'border-[#404040]'
                                            : 'border-muted-foreground/50'
                                        )}
                                      >
                                        {isSelected && (
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 16 16"
                                            fill="#404040"
                                            className="h-2.5 w-2.5"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                      <span className="truncate">
                                        {contribuinte.Nome} -{' '}
                                        {formatCNPJ(contribuinte.CNPJ_Contribuinte)}
                                      </span>
                                    </div>
                                  </SelectItem>
                                );
                              })
                            ) : searchEmpresa ? (
                              <div className="py-6 text-center text-sm text-muted-foreground">
                                Nenhuma empresa encontrada para "{searchEmpresa}"
                              </div>
                            ) : (
                              <SelectItem value="empty" disabled>
                                Nenhuma empresa disponível
                              </SelectItem>
                            )}
                          </div>
                        </SelectContent>
                      </Select>
                    )
                  ) : parametro.parametroOriginal.nome.toLowerCase().includes('ano') ? (
                    <Select
                      value={parametro.valor}
                      onValueChange={v => handleAtualizarValor(parametro.id, v)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Ano..." />
                      </SelectTrigger>
                      <SelectContent usePortal={false} className="z-10010 max-h-60">
                        {ANOS.map(a => (
                          <SelectItem key={a.value} value={a.value}>
                            {a.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : parametro.parametroOriginal.nome.toLowerCase().includes('mes') ? (
                    <Select
                      value={parametro.valor}
                      onValueChange={v => handleAtualizarValor(parametro.id, v)}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Mês..." />
                      </SelectTrigger>
                      <SelectContent usePortal={false} className="z-10010">
                        {MESES.map(m => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      className="flex-1"
                      placeholder={
                        parametro.parametroOriginal.descricao || parametro.parametroOriginal.nome
                      }
                      value={parametro.valor}
                      onChange={e => handleAtualizarValor(parametro.id, e.target.value)}
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={
                      isCaixaEntradaParam(parametro.parametroOriginal) && empresaTravadaPeloHeader
                    }
                    onClick={() => handleLimparParametro(parametro)}
                    className="text-destructive hover:text-destructive shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Tipo de agendamento</Label>
              <Select value={agendamentoTipoId} onValueChange={setAgendamentoTipoId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={isLoadingTipos ? 'Carregando tipos...' : 'Selecione um tipo'}
                  />
                </SelectTrigger>
                <SelectContent usePortal={false} className="z-10010">
                  {(tiposAgendamento ?? []).map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id.toString()}>
                      {tipo.Descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Esta é a última etapa. Todos os campos devem ser preenchidos para liberar a opção de
                agendamento.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 py-4 border-t border-border">
            <Button variant="outline" onClick={onFechar} className="flex-1 sm:flex-none">
              Cancelar
            </Button>
            <Button onClick={handleAgendar} className="flex-1 sm:flex-none" disabled={!podeAgendar}>
              {gravarAgendamento.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Agendando...
                </>
              ) : (
                'Agendar Processamento'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
